import React, { useRef, useEffect, useState } from "react";

/**
 * Wraps a table in a horizontally scrollable container with:
 * - a right-edge fade gradient (signals more content to the right)
 * - a thin styled scrollbar
 * - the gradient disappears when scrolled to the end
 *
 * Usage:
 *   <ScrollTable className="sales-table-wrap" wrapperStyle={...}>
 *     <table>...</table>
 *   </ScrollTable>
 */
export default function ScrollTable({ children, className = "", wrapperClass = "", wrapperStyle = {}, borderRadius = 12 }) {
  const scrollRef = useRef(null);
  const [atEnd, setAtEnd] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // within 4px of the end = consider "at end"
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll(); // initial check
    el.addEventListener("scroll", checkScroll, { passive: true });
    // also check on resize
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", checkScroll); ro.disconnect(); };
  }, []);

  return (
    <div
      className={`scroll-x-container${atEnd ? " scrolled-end" : ""} ${wrapperClass}`}
      style={{ borderRadius, ...wrapperStyle }}
    >
      <div
        ref={scrollRef}
        className={className}
        style={{ overflowX: "auto", borderRadius }}
      >
        {children}
      </div>
    </div>
  );
}
